import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Bookmark, Share2, MessageCircle, Play, Sparkles, MapPin, Briefcase, ChevronUp, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { playSparkSound } from '../lib/sparkSound';
import { DEMO_BUSINESSES } from '../lib/demoData';
import { useSubscription } from '../hooks/useSubscription';
import UpgradeModal from '../components/UpgradeModal';

const Feed = () => {
  const { user, profile } = useAuth();
  const { canCreateSpark, refresh: refreshSub } = useSubscription();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showingDemo, setShowingDemo] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showSparkAnim, setShowSparkAnim] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    let mounted = true;
    // Wait for auth to be established before querying — prevents race where
    // Feed fetches before session is fully hydrated (and RLS may reject).
    if (user === undefined) return; // still resolving
    const fetch = async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('*, owner:profiles!businesses_owner_id_fkey(id, full_name, avatar_url, location)')
        .eq('is_seeking', true)
        .not('name', 'eq', '')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[Feed] fetch error:', error.message, error);
      }
      if (mounted) {
        // Filter defensively (in case 'name' field is whitespace only)
        const list = (data || []).filter((b) => (b.name || '').trim().length > 0);
        if (list.length === 0) {
          setBusinesses(DEMO_BUSINESSES);
          setShowingDemo(true);
        } else {
          setBusinesses(list);
          setShowingDemo(false);
        }
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [user?.id]);

  const handleSpark = async (business) => {
    if (!user || profile?.role !== 'investor') return;
    if (business.owner_id === user.id) return;
    if (business.isDemo) {
      // Demo: anima sem gravar no banco
      setShowSparkAnim(true);
      playSparkSound().catch(() => {});
      setTimeout(() => setShowSparkAnim(false), 2800);
      return;
    }
    // Plan limit guard (investor free: 3 Sparks/month)
    if (!canCreateSpark) {
      setShowUpgrade(true);
      return;
    }
    try {
      const { error } = await supabase.from('sparks').insert({
        investor_id: user.id,
        business_id: business.id,
        entrepreneur_id: business.owner_id,
        status: 'pending',
      });
      if (error && error.code !== '23505') throw error;
      setShowSparkAnim(true);
      playSparkSound().catch(() => {});
      setTimeout(() => setShowSparkAnim(false), 2800);
      // refresh quota count
      refreshSub();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('spark error', e);
    }
  };

  const handleLike = async (business) => {
    if (!user || business.isDemo) return;
    await supabase.from('likes').upsert({ user_id: user.id, business_id: business.id });
  };

  return (
    <div className="min-h-[calc(100vh-200px)] max-w-2xl mx-auto px-3 sm:px-6 pt-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="heading-display text-2xl font-bold">Feed</h1>
          <p className="text-xs text-slate-400 mt-0.5">Descubra os negócios que estão procurando uma Spark</p>
        </div>
        {profile?.role === 'entrepreneur' && (
          <Link to="/app/business/new" className="sm:hidden btn-gold rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5">
            <Plus size={14} /> Postar
          </Link>
        )}
      </div>

      {loading && <div className="py-20 text-center text-slate-400 text-sm" data-testid="feed-loading">Carregando feed...</div>}

      {!loading && showingDemo && (
        <div
          className="glass-card rounded-2xl px-4 py-3 mb-5 flex items-center gap-3 border border-[#ffa726]/30"
          data-testid="feed-demo-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-[#ffa726]/15 text-[#ffd54f] flex items-center justify-center flex-shrink-0">
            <Sparkles size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-white">Visualização demo</div>
            <p className="text-[11px] text-slate-400 leading-snug">
              {profile?.role === 'entrepreneur'
                ? 'Estes são exemplos. Publique sua ideia para aparecer no feed real.'
                : 'Estes são negócios de exemplo. Em breve empreendedores reais aparecerão aqui.'}
            </p>
          </div>
        </div>
      )}

      {!loading && businesses.length === 0 && (
        <div className="glass-card rounded-3xl p-10 text-center">
          <Sparkles size={32} className="mx-auto text-[#ffd54f] mb-4" />
          <div className="text-lg font-bold">Ainda não temos negócios no feed</div>
          <p className="text-sm text-slate-400 mt-2">
            {profile?.role === 'entrepreneur'
              ? 'Seja o primeiro! Publique sua ideia agora.'
              : 'Aguarde — empreendedores estão chegando.'}
          </p>
          {profile?.role === 'entrepreneur' && (
            <Link to="/app/business/new" className="inline-flex items-center gap-2 btn-gold rounded-xl px-5 py-3 text-xs font-bold mt-5">
              <Plus size={14} /> Publicar minha ideia
            </Link>
          )}
        </div>
      )}

      <div className="space-y-5">
        {businesses.map((b, i) => (
          <FeedCard key={b.id} business={b} onSpark={() => handleSpark(b)} onLike={() => handleLike(b)} isInvestor={profile?.role === 'investor'} userId={user?.id} />
        ))}
      </div>

      <AnimatePresence>
        {showSparkAnim && <SparkBurst />}
      </AnimatePresence>
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} reason="spark_limit" />
    </div>
  );
};

const FeedCard = ({ business, onSpark, onLike, isInvestor, userId }) => {
  const [liked, setLiked] = useState(false);
  const isOwner = business.owner_id === userId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="relative glass-card rounded-3xl overflow-hidden"
    >
      {/* Media (video if has it, otherwise cover image) */}
      <div className="relative aspect-[9/16] sm:aspect-video bg-gradient-to-br from-slate-900 to-[#050816]">
        {business.video_url ? (
          <video src={business.video_url} poster={business.thumbnail_url || business.cover_image_url} className="w-full h-full object-cover" controls loop muted playsInline />
        ) : business.cover_image_url ? (
          <img src={business.cover_image_url} alt={business.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Sparkles size={48} className="text-slate-700" />
          </div>
        )}

        {business.is_seeking && (
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #4facfe, #1e90ff)', boxShadow: '0 0 20px rgba(79,172,254,0.5)' }}>
            <Briefcase size={11} /> Procurando Spark
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center font-bold text-sm">
            {business.owner?.avatar_url ? (
              <img src={business.owner.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : ((business.owner?.full_name || 'U').charAt(0).toUpperCase())}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{business.name}</div>
            <div className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
              <MapPin size={10} /> {[business.city, business.state].filter(Boolean).join(', ') || business.owner?.location || 'Brasil'}
            </div>
          </div>
          {business.funding_target > 0 && (
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-[#ffd54f]">Meta</div>
              <div className="text-sm font-bold gradient-text-gold">R$ {Math.round(business.funding_target/1000)}k</div>
            </div>
          )}
        </div>

        {business.tagline && <p className="text-sm text-white leading-relaxed mb-2">{business.tagline}</p>}
        {business.description && <p className="text-xs text-slate-400 leading-relaxed">{business.description}</p>}

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          <button onClick={() => { setLiked(!liked); onLike(); }} className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold ${liked ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>
            <Heart size={14} className={liked ? 'fill-red-500' : ''} /> Curtir
          </button>
          <button className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold bg-white/5 text-slate-300 hover:bg-white/10">
            <Bookmark size={14} /> Salvar
          </button>
          <div className="flex-1" />
          {isInvestor && !isOwner && (
            <button
              onClick={onSpark}
              data-testid={`spark-btn-${business.id}`}
              data-role="spark-button"
              title="Enviar Spark (Tenho Interesse)"
              className="btn-gold rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5"
            >
              <Sparkles size={14} /> Tenho Interesse
            </button>
          )}
          {isOwner && (
            <span className="text-[10px] text-slate-500 italic">sua publicação</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const SparkBurst = () => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
  >
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
    <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
      transition={{ type: 'spring', damping: 14, stiffness: 220 }}
      className="relative glass-card rounded-3xl px-8 py-7 text-center"
      style={{ boxShadow: '0 30px 100px rgba(79,172,254,0.4), 0 30px 100px rgba(255,167,38,0.3)' }}>
      {Array.from({ length: 18 }).map((_, i) => {
        const angle = (i / 18) * Math.PI * 2;
        const dist = 140;
        return (
          <motion.div key={i}
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, opacity: [0,1,0] }}
            transition={{ duration: 1.4, delay: 0.2 + (i % 5) * 0.05 }}
            className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full"
            style={{ background: i % 2 === 0 ? '#ffa726' : '#4facfe', boxShadow: i % 2 === 0 ? '0 0 8px #ffa726' : '0 0 8px #4facfe' }} />
        );
      })}
      <Sparkles size={40} className="mx-auto text-[#ffd54f] mb-2" />
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#ffd54f] font-semibold">⚡ enviado ⚡</div>
      <div className="mt-2 heading-display text-2xl font-bold gradient-text">Você acendeu a faísca!</div>
      <p className="mt-2 text-sm text-slate-300">O empreendedor agora pode aceitar e iniciar a conversa.</p>
    </motion.div>
  </motion.div>
);

export default Feed;
